import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { VEHICLE_CLASSES } from "./vehicleClasses";
import { VehicleSilhouette } from "./vehicleSilhouettes";

/**
 * Big tile picker for the Fahrzeugart sub-step.
 * 4–5 columns desktop, 2 columns ≤ 480 px (per the user's choice).
 */

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: "10px",
    "@media (min-width: 600px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
    "@media (min-width: 900px)": {
      gridTemplateColumns: "repeat(4, 1fr)",
      gap: "14px",
    },
    "@media (min-width: 1280px)": {
      gridTemplateColumns: "repeat(5, 1fr)",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.cardBg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    rowGap: "6px",
    paddingTop: "16px",
    paddingBottom: "14px",
    paddingLeft: "8px",
    paddingRight: "8px",
    minHeight: "120px",
    textAlign: "center",
    ...shorthands.border("2px", "solid", BRAND.border),
    ...shorthands.borderRadius("10px"),
    transitionProperty: "border-color, box-shadow, transform, background-color",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor("rgba(230,106,44,0.6)"),
      transform: "translateY(-2px)",
    },
  },
  tileActive: {
    ...shorthands.borderColor(BRAND.pillarOrange),
    backgroundColor: "rgba(230,106,44,0.06)",
    boxShadow: "0 0 0 4px rgba(230,106,44,0.18)",
  },
  iconWrap: {
    color: BRAND.pillarOrange,
    width: "56px",
    height: "36px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  short: {
    fontSize: "0.92rem",
    fontWeight: 700,
    color: BRAND.ink,
  },
  desc: {
    fontSize: "0.74rem",
    color: BRAND.muted,
    lineHeight: 1.25,
  },
});

// Silhouetten liegen in vehicleSilhouettes.tsx — sie werden auch von der
// Kfz-Objektmappe im Risiken-Reiter gebraucht.

type Props = {
  value: string;
  onChange: (value: string) => void;
};

const VehicleClassPicker = ({ value, onChange }: Props) => {
  const styles = useStyles();
  return (
    <div className={styles.grid}>
      {VEHICLE_CLASSES.map((c) => {
        const active = value === c.value;
        return (
          <button
            key={c.value}
            type="button"
            onClick={() => onChange(c.value)}
            className={mergeClasses(styles.tile, active && styles.tileActive)}
            aria-pressed={active}
          >
            <span className={styles.iconWrap}>
              <VehicleSilhouette iconKey={c.iconKey} />
            </span>
            <span className={styles.short}>{c.short}</span>
            <span className={styles.desc}>{c.description}</span>
          </button>
        );
      })}
    </div>
  );
};

export default VehicleClassPicker;
